import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import UnitSelector from './UnitSelector';
import ChatRoom from './ChatRoom';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import {
  fetchAccessibleChatRooms,
  getChatMessageUnitId,
  isChatAdmin,
} from '../../services/chatService';
import { getRoomDisplayName } from '../../services/chatListGroups';
import ModulePageHeader from '../common/ModulePageHeader';
import useBrowserBackStack from '../../hooks/useBrowserBackStack';
import './Chat.css';

function Chat() {
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [accessibleRooms, setAccessibleRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();

  const isAdmin = isChatAdmin({ memberships, tenantId, legacyUser: user });
  const hasMultipleRooms = accessibleRooms.length > 1;
  const showBackButton = Boolean(selectedUnit && hasMultipleRooms);
  const resetToRoomList = useCallback(() => {
    setSelectedUnit(null);
  }, []);
  const closeRoomDetail = useBrowserBackStack(showBackButton, resetToRoomList);

  useEffect(() => {
    if (!user || !tenantId) {
      return undefined;
    }

    let cancelled = false;
    setRoomsLoading(true);

    fetchAccessibleChatRooms({ tenantId, memberships, legacyUser: user })
      .then((rooms) => {
        if (cancelled) {
          return;
        }

        setAccessibleRooms(rooms);

        if (rooms.length === 1) {
          setSelectedUnit(rooms[0]);
        } else {
          setSelectedUnit(null);
        }
      })
      .catch((error) => {
        console.error('Error fetching chat rooms:', error);
        if (!cancelled) {
          setAccessibleRooms([]);
          setSelectedUnit(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setRoomsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, tenantId, memberships]);

  const handleSelectUnit = (unit) => {
    setSelectedUnit(unit);
  };

  const renderContent = () => {
    if (roomsLoading) {
      return (
        <div className="unit-list-loading">
          <Spinner animation="border" variant="primary" size="sm" />
        </div>
      );
    }

    if (accessibleRooms.length === 0) {
      return <p className="chat-empty-state">Ni razpoložljivih klepetalnic.</p>;
    }

    if (selectedUnit) {
      return <ChatRoom unitId={getChatMessageUnitId(selectedUnit)} />;
    }

    return <UnitSelector onSelectUnit={handleSelectUnit} />;
  };

  const showRoomList = !roomsLoading && accessibleRooms.length > 0 && !selectedUnit;
  const headerTitle = selectedUnit
    ? getRoomDisplayName(selectedUnit)
    : 'Klepetalnica';

  return (
    <div className="platform-module-container chat-container">
        <ModulePageHeader
          eyebrow={selectedUnit ? 'Klepetalnica' : undefined}
          title={headerTitle}
          subtitle={
            showRoomList
              ? 'Izberite klepetalnico za pogovor s člani sindikata.'
              : undefined
          }
          actions={(
            <>
              {isAdmin && !selectedUnit && (
                <Button variant="primary" onClick={() => navigate('/chat/manage')}>
                  Upravljanje klepetalnic
                </Button>
              )}
              {showBackButton && (
                <Button variant="primary" onClick={closeRoomDetail}>
                  Nazaj na izbiro
                </Button>
              )}
            </>
          )}
        />

        {renderContent()}
    </div>
  );
}

export default Chat;
